import { useState, useCallback } from 'react';
// @mui
import { useTheme, styled } from '@mui/material/styles';
import NoSsr from '@mui/material/NoSsr';
import Typography from '@mui/material/Typography';
// components
import Iconify from 'src/components/iconify';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { GOOGLE_BUSINESS_PLACE_ID, GOOGLE_MAPS_API_KEY } from 'src/config-global';

// ----------------------------------------------------------------------

const StyledRoot = styled('div')(({ theme }) => ({
  zIndex: 0,
  height: 560,
  overflow: 'hidden',
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
}));

const containerStyle = {
  width: '100%',
  height: '100%',
};

// ----------------------------------------------------------------------
export default function ContactMap() {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  // eslint-disable-next-line no-unused-vars
  const [map, setMap] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [placeDetails, setPlaceDetails] = useState(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);

    const service = new window.google.maps.places.PlacesService(mapInstance);
    const request = {
      placeId: GOOGLE_BUSINESS_PLACE_ID,
      fields: ['name', 'formatted_address', 'formatted_phone_number', 'geometry'],
    };

    service.getDetails(request, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
        setPlaceDetails(place);
      }
    });
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  if (!isLoaded) return <div>Loading...</div>;

  // Use the place's location if available; otherwise fallback to a default center.
  const markerPosition = placeDetails
    ? placeDetails.geometry.location
    : { lat: 33.87875, lng: 35.53542 };

  return (
    <NoSsr>
      <StyledRoot>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={markerPosition}
          zoom={15}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            styles: isLight
              ? []
              : [
                  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
                  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
                  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
                ],
          }}
        >
          <Marker position={markerPosition} onClick={() => setSelectedMarker(markerPosition)} />

          {selectedMarker && (
            <InfoWindow position={selectedMarker} onCloseClick={() => setSelectedMarker(null)}>
              <div>
                {placeDetails ? (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      {placeDetails.name}
                    </Typography>
                    <Typography component="div" variant="caption">
                      {placeDetails.formatted_address}
                    </Typography>
                    {placeDetails.formatted_phone_number && (
                      <Typography
                        component="div"
                        variant="caption"
                        sx={{ mt: 1, display: 'flex', alignItems: 'center' }}
                      >
                        <Iconify icon="solar:phone-bold" width={14} sx={{ mr: 0.5 }} />
                        {placeDetails.formatted_phone_number}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography component="div" variant="caption">
                    Loading details...
                  </Typography>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </StyledRoot>
    </NoSsr>
  );
}
